import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Logo from '../components/Logo';

function SetupAdmin() {
  const navigate = useNavigate();
  const expectedToken = useMemo(() => (process.env.REACT_APP_ADMIN_SETUP_TOKEN || '').trim(), []);

  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!expectedToken) {
      setError('Admin setup is disabled. Add REACT_APP_ADMIN_SETUP_TOKEN to your .env, then restart the dev server.');
      return;
    }

    if (token.trim() !== expectedToken) {
      setError('Invalid setup token.');
      return;
    }

    if (!email.trim()) {
      setError('Please enter an email address.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: email.trim(),
        createdAt: serverTimestamp(),
        status: 'active',
        role: 'admin'
      });

      setSuccess('Admin account created. Redirecting to Admin Dashboard…');
      setTimeout(() => navigate('/admin', { replace: true }), 600);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        setError('That email is already in use. Try signing in instead.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/weak-password') {
        setError('Password is too weak. Try a longer password.');
      } else {
        setError(err?.message || 'Failed to create admin account.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 relative">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(34,211,238,0.12),transparent_55%),radial-gradient(700px_circle_at_90%_20%,rgba(56,189,248,0.08),transparent_55%)]" />
      <Navigation />
      <div className="relative flex items-center justify-center px-4 py-20 min-h-[80vh]">
        <motion.div
          className="w-full max-w-md card-glass rounded-3xl border border-white/10 p-8 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col items-center mb-8">
            <Logo darkMode={true} />
            <h2 className="mt-6 text-center text-3xl font-semibold tracking-tight text-white">
              Admin setup
            </h2>
            <p className="mt-2 text-center text-sm text-stone-400">
              Create the first admin account for this project.
            </p>
          </div>

          {error && (
            <div className="rounded-xl bg-rose-500/10 p-4 border border-rose-400/25 mb-4">
              <p className="text-sm text-rose-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="rounded-xl bg-emerald-500/10 p-4 border border-emerald-400/25 mb-4">
              <p className="text-sm text-emerald-200">{success}</p>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1">
                Setup token
              </label>
              <input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="input-glass mt-1 block w-full placeholder:text-stone-500"
                placeholder="REACT_APP_ADMIN_SETUP_TOKEN"
                autoComplete="off"
              />
              <p className="mt-2 text-xs text-stone-500">
                Tip: after creating the admin, remove the token from your environment.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1">
                Admin email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-glass mt-1 block w-full placeholder:text-stone-500"
                placeholder="admin@yourdomain.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-glass mt-1 block w-full placeholder:text-stone-500"
                placeholder="Create a strong password"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1">
                Confirm password
              </label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-glass mt-1 block w-full placeholder:text-stone-500"
                placeholder="Repeat password"
                autoComplete="new-password"
              />
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full flex justify-center rounded-xl bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isLoading ? 'Creating…' : 'Create admin'}
            </motion.button>

            <div className="text-center text-sm text-stone-400">
              Already have an admin?{' '}
              <Link to="/login" className="font-medium text-cyan-300 hover:text-cyan-200">
                Sign in
              </Link>
            </div>
          </form>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}

export default SetupAdmin;

